import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Plus, Edit3, Trash2, Search, X, Check } from 'lucide-react';

// Selo do CRECI: o portal de vendas confere na consulta pública do CRECI-PR pelo CPF (só informa, não bloqueia).
const SELO = {
  verificado: ['CRECI verificado', 'bg-green-100 text-green-800'],
  irregular: ['CRECI irregular', 'bg-red-100 text-red-800'],
  nao_encontrado: ['Não encontrado no CRECI-PR', 'bg-red-100 text-red-800'],
  sem_cpf: ['Sem CPF válido', 'bg-amber-100 text-amber-800'],
  erro: ['Consulta falhou', 'bg-amber-100 text-amber-800'],
};
const soNumero = (x) => String(x || '').replace(/\D/g, '').replace(/^0+/, '');
function SeloCreci({ selo, detalhe }) {
  if (selo === undefined) return null; // verificação ainda não existe neste banco
  if (!selo) return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500">Ainda não verificado</span>;
  const [rot, cor] = SELO[selo.status] || SELO.erro;
  const texto = selo.status === 'irregular' && selo.situacao ? `CRECI ${selo.situacao.toLowerCase()}` : rot;
  const quando = selo.consultado_em ? new Date(selo.consultado_em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : null;
  const dica = [selo.nome_oficial, selo.creci_oficial && `${selo.creci_oficial} · ${selo.situacao || ''}`, quando && `consultado em ${quando}`].filter(Boolean).join(' — ');
  const corrigido = selo.creci_anterior && soNumero(selo.creci_anterior) !== soNumero(selo.creci_oficial);
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span title={dica} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold w-fit ${cor}`}>{selo.status === 'verificado' ? '✓ ' : ''}{texto}</span>
      {detalhe && dica && <span className="text-[11px] text-gray-500">{dica}</span>}
      {corrigido && <span className="text-[11px] text-amber-700">número corrigido (era “{selo.creci_anterior}”)</span>}
    </span>
  );
}

function CorretorForm({ initial, onSave, onCancel, imobiliarias }) {
  const [form, setForm] = useState(initial || { nome: '', cpf: '', creci: '', email: '', whatsapp: '', tipo: 'autonomo', imobiliaria_id: '', cargo: 'corretor', observacoes: '', comissao_percentual: '' });
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  // creci_selo vem da lista (só leitura) — não volta no salvar
  const salvar = () => { const { creci_selo, ...dados } = form; onSave({ ...dados, data_nascimento: dados.data_nascimento ? String(dados.data_nascimento).slice(0, 10) : null, comissao_percentual: dados.comissao_percentual === '' ? null : dados.comissao_percentual }); };
  return (
    <div className="bg-white rounded-xl shadow p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome *</label><input className="input-field" value={form.nome} onChange={set('nome')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input className="input-field" value={form.cpf} onChange={set('cpf')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CRECI</label><input className="input-field" value={form.creci || ''} onChange={set('creci')} />
          {initial && <div className="mt-1"><SeloCreci selo={form.creci_selo} detalhe /></div>}
        </div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data de nascimento</label><input type="date" className="input-field" value={form.data_nascimento ? String(form.data_nascimento).slice(0, 10) : ''} onChange={set('data_nascimento')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label><input className="input-field" value={form.email} onChange={set('email')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label><input className="input-field" value={form.whatsapp} onChange={set('whatsapp')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tipo *</label>
          <select className="input-field" value={form.tipo} onChange={set('tipo')}>
            <option value="autonomo">Autônomo</option>
            <option value="imobiliaria">Vinculado a Imobiliária</option>
          </select>
        </div>
        {form.tipo === 'imobiliaria' && (
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Imobiliária</label>
            <select className="input-field" value={form.imobiliaria_id || ''} onChange={set('imobiliaria_id')}>
              <option value="">Selecione...</option>
              {(imobiliarias || []).map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
            </select>
          </div>
        )}
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cargo *</label>
          <select className="input-field" value={form.cargo || 'corretor'} onChange={set('cargo')}>
            <option value="corretor">Corretor</option>
            <option value="gestor">Gestor</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">% de comissão</label>
          <input type="number" step="0.01" min="0" max="100" className="input-field" value={form.comissao_percentual ?? ''} onChange={set('comissao_percentual')} placeholder={form.tipo === 'imobiliaria' ? 'Deixe em branco pra usar o % da imobiliária' : 'Ex: 5'} />
        </div>
      </div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Observações</label>
        <textarea className="input-field" rows={3} value={form.observacoes || ''} onChange={set('observacoes')} />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={salvar} className="btn-primary">Salvar</button>
        <button onClick={onCancel} className="btn-secondary">Cancelar</button>
      </div>
    </div>
  );
}

export default function CorretoresList() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ['corretores', search], queryFn: () => api.get('/corretores', { params: { search } }).then(r => r.data.data) });
  const { data: imobs } = useQuery({ queryKey: ['imobiliarias'], queryFn: () => api.get('/imobiliarias').then(r => r.data.data) });
  const createMut = useMutation({ mutationFn: (d) => api.post('/corretores', d), onSuccess: () => { qc.invalidateQueries(['corretores']); setCreating(false); } });
  const updateMut = useMutation({ mutationFn: ({ id, ...d }) => api.put(`/corretores/${id}`, d), onSuccess: () => { qc.invalidateQueries(['corretores']); setEditing(null); } });
  const deleteMut = useMutation({ mutationFn: (id) => api.delete(`/corretores/${id}`), onSuccess: () => qc.invalidateQueries(['corretores']) });
  const aprovarMut = useMutation({ mutationFn: (userId) => api.patch(`/usuarios-vendas/${userId}/aprovar`), onSuccess: () => qc.invalidateQueries(['corretores']) });
  const rejeitarMut = useMutation({ mutationFn: (userId) => api.patch(`/usuarios-vendas/${userId}/rejeitar`), onSuccess: () => qc.invalidateQueries(['corretores']) });

  // Mostra também cadastros feitos no portal de vendas ainda não aprovados (status='pendente'),
  // que antes ficavam escondidos aqui (mesmo filtro de ativo escondia tanto o "desativado
  // manualmente" quanto o "aguardando aprovação") — só some da lista quem foi desativado ou
  // rejeitado de fato.
  const visiveis = (data || []).filter(c => c.ativo !== false || c.status === 'pendente');

  if (creating) return (<div><div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Novo Corretor</h1></div><CorretorForm imobiliarias={imobs} onSave={(d) => createMut.mutate(d)} onCancel={() => setCreating(false)} /></div>);
  if (editing) return (<div><div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Editar Corretor</h1></div><CorretorForm initial={editing} imobiliarias={imobs} onSave={(d) => updateMut.mutate({ id: editing.id, ...d })} onCancel={() => setEditing(null)} /></div>);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Corretores</h1>
        <button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Novo Corretor</button>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />
          <input className="input-field pl-10" placeholder="Buscar por nome, CPF ou CRECI..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-3 text-gray-400"><X size={16} /></button>}
        </div>
        {!isLoading && (
          <span className="shrink-0 px-3 py-2 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
            {visiveis.length} {visiveis.length === 1 ? 'corretor cadastrado' : 'corretores cadastrados'}
          </span>
        )}
      </div>
      {isLoading ? <div className="text-center text-gray-400 py-12">Carregando...</div> : (
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">CRECI</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Tipo</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Cargo</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Imobiliária</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">WhatsApp</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {visiveis.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{c.nome}</td>
                  <td className="px-4 py-3 text-gray-500"><div>{c.creci || '—'}</div><div className="mt-1"><SeloCreci selo={c.creci_selo} /></div></td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.tipo === 'autonomo' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{c.tipo === 'autonomo' ? 'Autônomo' : 'Imobiliária'}</span></td>
                  <td className="px-4 py-3">{c.cargo === 'gestor' ? <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Gestor</span> : <span className="text-gray-500">Corretor</span>}</td>
                  <td className="px-4 py-3 text-gray-500">{c.imobiliaria_nome || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{c.whatsapp || '—'}</td>
                  <td className="px-4 py-3">
                    {c.status === 'pendente'
                      ? <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">Pendente de aprovação</span>
                      : <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Aprovado</span>}
                  </td>
                  <td className="px-4 py-3"><div className="flex gap-2 items-center">
                    {c.status === 'pendente' && c.user_id && (
                      <>
                        <button title="Aprovar cadastro" onClick={() => aprovarMut.mutate(c.user_id)} className="text-green-600 hover:text-green-800"><Check size={15} /></button>
                        <button title="Rejeitar cadastro" onClick={() => { if (window.confirm('Rejeitar este cadastro?')) rejeitarMut.mutate(c.user_id); }} className="text-red-500 hover:text-red-700"><X size={15} /></button>
                      </>
                    )}
                    <button onClick={() => setEditing(c)} className="text-blue-600 hover:text-blue-800"><Edit3 size={15} /></button>
                    <button onClick={() => { if (window.confirm('Desativar este corretor?')) deleteMut.mutate(c.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {visiveis.length === 0 && <div className="text-center text-gray-400 py-8">Nenhum corretor encontrado.</div>}
        </div>
      )}
    </div>
  );
}
